import { useState, useMemo } from "react";
import { Send, Plus, Trash2, Save, Settings, Smile, Meh, Frown, MessageCircle } from "lucide-react";
import Badge from "../components/Badge";
import GuideBanner from "../components/GuideBanner";
import { fmtDate, getToday, waLink } from "../utils";

const trimestreInicio = () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().split("T")[0]; };

function KpiCard({ label, value, color, small }) {
  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", border: "1px solid #e5e7eb", borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</div>
    </div>
  );
}

export default function Satisfaction({ satisfaction = [], today, search, onSendPesquisa, pesquisaConfig = [], setPesquisaConfig }) {
  const [audience, setAudience] = useState("cliente");
  const [showEditor, setShowEditor] = useState(false);

  const list = useMemo(() => {
    let l = satisfaction.filter((s) => (s.audience || "cliente") === audience);
    if (search) { const q = search.toLowerCase(); l = l.filter((s) => (s.clientName || s.respondenteNome || "").toLowerCase().includes(q) || (s.feedback || "").toLowerCase().includes(q)); }
    return l.sort((a, b) => (b.surveyDate || b.createdAt || "").localeCompare(a.surveyDate || a.createdAt || ""));
  }, [satisfaction, audience, search]);

  const nps = useMemo(() => {
    const base = satisfaction.filter((s) => (s.audience || "cliente") === audience && s.score != null);
    const total = base.length;
    if (!total) return { score: 0, prom: 0, neut: 0, detr: 0, total: 0, tri: 0 };
    const prom = base.filter((s) => s.score >= 9).length;
    const detr = base.filter((s) => s.score <= 6).length;
    const neut = total - prom - detr;
    const ini = trimestreInicio();
    const tri = base.filter((s) => (s.createdAt || s.surveyDate || "") >= ini).length;
    return { score: Math.round((prom / total) * 100 - (detr / total) * 100), prom, neut, detr, total, tri };
  }, [satisfaction, audience]);

  const cfg = pesquisaConfig.find((c) => c.id === audience);
  const perguntas = cfg?.perguntas || [];

  const updatePerguntas = (novo, titulo) => {
    if (!setPesquisaConfig) return;
    setPesquisaConfig((prev) => prev.map((c) => c.id === audience ? { ...c, perguntas: novo, titulo: titulo ?? c.titulo, updatedAt: getToday() } : c));
  };
  const addPergunta = () => updatePerguntas([...perguntas, { id: `q${Date.now()}`, label: "Nova pergunta", tipo: "texto" }]);
  const editPergunta = (i, patch) => updatePerguntas(perguntas.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removePergunta = (i) => updatePerguntas(perguntas.filter((_, idx) => idx !== i));

  const modeBtn = (on) => ({ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569" });
  const inp = { padding: "8px 10px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Satisfação & NPS — Cliente e Fornecedor"
        text="Envie a pesquisa por link (WhatsApp/e-mail). Cada público tem seu formulário próprio, com perguntas editáveis + nota NPS. Rotacione entre clientes e fornecedores a cada 3 meses para coletar melhorias e registros técnicos."
      />

      {/* Alternador de público + ações */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <div onClick={() => setAudience("cliente")} style={modeBtn(audience === "cliente")}>Clientes</div>
          <div onClick={() => setAudience("fornecedor")} style={modeBtn(audience === "fornecedor")}>Fornecedores</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setShowEditor((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Settings size={13} /> Editar perguntas
          </button>
          <button onClick={onSendPesquisa} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Send size={14} /> Enviar pesquisa
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        <KpiCard label={`NPS ${audience === "cliente" ? "Clientes" : "Fornecedores"}`} value={nps.score} color={nps.score >= 50 ? "#059669" : nps.score >= 0 ? "#f59e0b" : "#ef4444"} />
        <KpiCard label="Respostas" value={nps.total} color="#7c3aed" />
        <KpiCard label="Promotores" value={nps.prom} color="#059669" small />
        <KpiCard label="Neutros" value={nps.neut} color="#f59e0b" small />
        <KpiCard label="Detratores" value={nps.detr} color="#ef4444" small />
        <KpiCard label="Últimos 3 meses" value={nps.tri} color="#0d9488" small />
      </div>

      {/* Editor de perguntas */}
      {showEditor && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Perguntas — {audience === "cliente" ? "Clientes" : "Fornecedores"}</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>A nota NPS (0–10) é fixa e sempre aparece primeiro. Abaixo, edite as demais perguntas.{audience === "fornecedor" ? " O fornecedor também informa quais clientes atende." : ""}</div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>Título do formulário</div>
            <input style={inp} value={cfg?.titulo || ""} onChange={(e) => updatePerguntas(perguntas, e.target.value)} placeholder="Título" />
          </div>
          {perguntas.map((q, i) => (
            <div key={q.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: "#94a3b8", width: 18 }}>{i + 1}.</span>
              <input style={{ ...inp, flex: 1 }} value={q.label} onChange={(e) => editPergunta(i, { label: e.target.value })} />
              <select style={{ ...inp, width: 130, flexShrink: 0, cursor: "pointer" }} value={q.tipo} onChange={(e) => editPergunta(i, { tipo: e.target.value })}>
                <option value="texto">Texto livre</option>
                <option value="escala">Nota 1–5</option>
              </select>
              <button onClick={() => removePergunta(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", flexShrink: 0 }}><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={addPergunta} style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8, padding: "7px 12px", borderRadius: 7, border: "1px dashed #cbd5e1", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={13} /> Adicionar pergunta
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 11, color: "#059669" }}>
            <Save size={13} /> As alterações são salvas automaticamente e valem para os próximos envios.
          </div>
        </div>
      )}

      {/* Lista de respostas */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13, background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb" }}>Nenhuma resposta ainda. Envie a pesquisa pelo botão acima.</div>}
        {list.map((s) => {
          const color = s.score >= 9 ? "#059669" : s.score >= 7 ? "#f59e0b" : "#ef4444";
          const Icon = s.score >= 9 ? Smile : s.score >= 7 ? Meh : Frown;
          const wa = waLink(s.respondenteContato);
          return (
            <div key={s.id} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", borderLeft: `4px solid ${color}`, padding: "12px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon size={18} color={color} /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{s.clientName || s.respondenteNome || "Anônimo"} <span style={{ color, fontWeight: 800 }}>· {s.score}/10</span></div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{fmtDate(s.surveyDate || s.createdAt)}{s.origem === "link" ? " · via link" : ""}{s.respondenteNome && s.clientName ? ` · ${s.respondenteNome}` : ""}</div>
                  </div>
                </div>
                {wa && <button onClick={() => window.open(wa, "_blank")} style={{ background: "none", border: "none", cursor: "pointer", color: "#059669", flexShrink: 0 }} title="WhatsApp"><MessageCircle size={16} /></button>}
              </div>
              {(s.respostas?.length > 0 || s.feedback || s.clientesAtendidos) && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 4 }}>
                  {(s.respostas || []).filter((r) => r.resposta !== "" && r.resposta != null).map((r, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#475569" }}><strong style={{ color: "#334155" }}>{r.label}:</strong> {r.tipo === "escala" ? `${r.resposta}/5` : r.resposta}</div>
                  ))}
                  {s.clientesAtendidos && <div style={{ fontSize: 11, color: "#475569" }}><strong style={{ color: "#334155" }}>Clientes atendidos:</strong> {s.clientesAtendidos}</div>}
                  {s.feedback && !(s.respostas?.length) && <div style={{ fontSize: 11, color: "#475569" }}>{s.feedback}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
